import {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
} from 'react';
import { setLanguage, getLanguage } from '$core/helpers/global';
import { httpGet } from '$core/helpers/xhr';

export interface GlobalContextValues {
  language: string;
  updateLanguage: (lang: string) => void;
  storeUuid: string | null;
  refreshStore: () => void;
}

const AppContext = createContext<GlobalContextValues | undefined>(undefined);

type AppProviderProps = {
  children: React.ReactNode;
};

export const AppProvider = ({ children }: AppProviderProps) => {
  const DEFAULT_STORE_UUID = '1';

  const [language, setLanguageState] = useState<string>(getLanguage());
  const [storeUuid, setStoreUuid] = useState<string | null>(DEFAULT_STORE_UUID);

  const updateLanguage = (lang: string) => {
    setLanguageState(lang);
    setLanguage(lang);
  };

  const fetchUserStore = useCallback(async () => {
    try {
      // @ts-ignore
      const user = window.drupalSettings?.user;

      const uuid = user?.uuid;
      const uid = user?.uid;

      // Utente anonimo o non identificabile -> fallback
      if (!uid || uid === 0 || uid === '0') {
        setStoreUuid(DEFAULT_STORE_UUID);
        return;
      }

      // const endpoint = uuid
      //   ? `/user/user/${uuid}?include=field_user_store`
      //   : `/user/user?filter[uid]=${uid}&include=field_user_store`;

      const endpoint = uuid
        ? `/user/user/${uuid}`
        : `/user/user?filter[uid]=${uid}`;

      const response = await httpGet(endpoint);
      const { data } = response;

      let userData = null;

      if (uuid) {
        userData = data.data;
      } else if (data.data && data.data.length > 0) {
        [userData] = data.data;
      }

      if (!userData) {
        setStoreUuid(DEFAULT_STORE_UUID);
        return;
      }

      const storeInternalId = userData.field_user_store?.meta?.drupal_internal__target_id;

      let storeId = null;

      if (userData.relationships?.field_user_store?.data?.id) {
        storeId = userData.relationships.field_user_store.data.id;
      } else if (userData.relationships?.['field-user-store']?.data?.id) {
        storeId = userData.relationships['field-user-store'].data.id;
      } else if (userData.field_user_store?.id) {
        storeId = userData.field_user_store.id;
      } else if (userData['field-user-store']?.id) {
        storeId = userData['field-user-store'].id;
      }

      if (!storeId || storeInternalId == null) {
        console.warn('Store UUID non trovato nel payload utente. Uso fallback storeUuid=2.');
        setStoreUuid(DEFAULT_STORE_UUID);
        return;
      }

      setStoreUuid(String(storeInternalId));
    } catch (error) {
      console.warn('Impossibile recuperare lo store utente. Uso fallback storeUuid=2.', error);
      setStoreUuid(DEFAULT_STORE_UUID);
    }
  }, []);

  useEffect(() => {
    fetchUserStore();

    window.addEventListener('storage', fetchUserStore);
    return () => window.removeEventListener('storage', fetchUserStore);
  }, [fetchUserStore]);

  const contextValue = useMemo(() => ({
    language,
    updateLanguage,
    storeUuid,
    refreshStore: fetchUserStore,
  }), [language, storeUuid, fetchUserStore]);

  return (
    <AppContext.Provider value={contextValue}>
      {children}
    </AppContext.Provider>
  );
};

export const useGlobalContext = () => {
  const ctx = useContext(AppContext);
  if (!ctx) {
    throw new Error('useGlobalContext must be used within <AppProvider>');
  }
  return ctx;
};
